import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { forwardRef, memo, useEffect, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import { mergeSx } from "../system";
import { TextInputBase, textInputBaseClasses } from "./TextInputBase";
export const multilineTextFieldClasses = {
    root: "MinervaTextField-root",
};
export const useAutoSizeTextArea = (ref, value, options) => {
    useEffect(() => {
        if (ref.current) {
            // Get the font size. (Defaults to 16px)
            const fontSize = typeof window !== "undefined"
                ? window.getComputedStyle(ref.current).getPropertyValue("font-size")
                : "16px";
            // Temporary set height to 0px to get the full scrollHeight
            ref.current.style.height = "0px";
            // Get the full scroll height and reset the height
            ref.current.style.height = options?.rows
                ? `${Number.parseFloat(fontSize) * options.rows + options.rows * 4}px`
                : `${ref.current.scrollHeight}px`;
            if (options?.minRows) {
                ref.current.style.minHeight = `${Number.parseFloat(fontSize) * options.minRows + options.minRows * 4}px`;
            }
            if (options?.maxRows) {
                ref.current.style.maxHeight = `${Number.parseFloat(fontSize) * options.maxRows + options.maxRows * 4}px`;
            }
        }
    }, [ref, value, options]);
};
/**
 * ## Definition
 * A MultilineTextField is a text input that allows for multiple lines of text. They are most commonly used for long user input.
 *
 * ## Behavior
 * - The `MultilineTextField` component is a controlled or uncontrolled component.
 * - The `MultilineTextField` component supports the `rows`, `minRows`, and `maxRows` properties to control the number of rows the textarea should have. Please see the description of these properties for more information on how to utilize them.
 *
 * ## Structure
 * - Based off of the `TextInputBase` component with style modifications to support multiline text and accommodate the MultilineTextField spec.
 * - The DOM node of the `MultilineTextField` is a textarea element. This is done by setting the polymorphic `as` prop on the `TextInputBase` to textarea.
 *
 * ## Technical Limitations
 * - The `MultilineTextField` is a native textarea element and does not support text formatting.
 * - In order for the input to scale with the value, the input node is given a scrollbar gutter of `stable` such that the scrollbar width is taken into account when calculating the scrollHeight of the text. This slightly shortens the width of the input area.
 */
export const MultilineTextField = memo(forwardRef(({ value: _value, rows, minRows = 2, maxRows, className, sx, onChange, ...rest }, ref) => {
    const [value, setValue] = useState(_value ?? "");
    const inputRef = useRef(null);
    useAutoSizeTextArea(inputRef, value, {
        rows,
        minRows,
        maxRows,
    });
    useEffect(() => {
        setValue(_value ?? "");
    }, [_value]);
    return (_jsx(TextInputBase, { as: "textarea", ref: mergeRefs([inputRef, ref]), value: value, className: clsx([multilineTextFieldClasses.root, className]), onChange: (e) => {
            onChange?.(e, e.target.value);
            setValue(e.target.value);
        }, sx: mergeSx({
            [`& .${textInputBaseClasses.input}`]: {
                resize: "none",
                height: "100%",
                minHeight: 4.5,
                scrollbarGutter: "stable",
            },
            [`& .${textInputBaseClasses["start-slot"]}, .${textInputBaseClasses["end-slot"]}`]: {
                paddingTop: 3,
                alignSelf: "flex-start",
            },
            [`& .${textInputBaseClasses["input-wrapper"]}`]: {
                paddingY: 3,
            },
        }, sx), ...rest }));
}));
//# sourceMappingURL=MultilineTextField.js.map